import React from "react";

import { Column, ColumnContainer, ColumnsContainer } from "../styles";
import { useOpticsStore } from "../../../../stores/use-optics-store";
import { macroStores } from "../../../../stores/export-stores";
import { MACRO_KEY } from "../../../defaults";
import { CARD_DESIGN_COLUMNS } from "../../drawers/settings/features/cards/defaults";

const STATE_KEYS = {
  [MACRO_KEY.CARDS]: {
    dataKey: "data",
    updateConfigKey: "updateCardsConfig",
    columnsKey: "columns",
    standaloneConfig: false,
    hasConfig: true,
  },
  [MACRO_KEY.ANNOUNCEMENTS]: {
    dataKey: "data",
    updateConfigKey: "updateConfig",
    columnsKey: "columns",
    standaloneConfig: true,
    hasConfig: true,
  },
  [MACRO_KEY.PEOPLE]: {
    dataKey: "data",
    updateConfigKey: "updateConfig",
    columnsKey: "columns",
    standaloneConfig: false,
    hasConfig: true,
    shouldShowChecker: { key: "variant", value: "card" },
  },
  [MACRO_KEY.NEWS]: {
    dataKey: "data",
    updateConfigKey: "updateConfig",
    columnsKey: "columns",
    standaloneConfig: false,
    hasConfig: true,
    shouldShowChecker: { key: "variant", value: ["card", "short_list"] },
  },
  [MACRO_KEY.PAGES]: {
    dataKey: "data",
    updateConfigKey: "updateConfig",
    columnsKey: "columns",
    standaloneConfig: false,
    hasConfig: true,
    shouldShowChecker: { key: "variant", value: ["card", "mini_card"] },
  },
  [MACRO_KEY.SPACES]: {
    dataKey: "data",
    updateConfigKey: "updateConfig",
    columnsKey: "columns",
    standaloneConfig: false,
    hasConfig: true,
    shouldShowChecker: { key: "variant", value: "card" },
  },
  [MACRO_KEY.RECENTLY_ACTIVE]: {
    dataKey: "data",
    updateConfigKey: "updateData",
    columnsKey: "columns",
    standaloneConfig: false,
    hasConfig: false,
    shouldShowChecker: { key: "view", value: "card" },
  },
  [MACRO_KEY.UPCOMING_BIRTHDAYS]: {
    dataKey: "data",
    updateConfigKey: "updateData",
    columnsKey: "columns",
    standaloneConfig: false,
    hasConfig: false,
    shouldShowChecker: { key: "view", value: "card" },
  },
  [MACRO_KEY.NEW_STARTERS]: {
    dataKey: "data",
    updateConfigKey: "updateData",
    columnsKey: "columns",
    standaloneConfig: false,
    hasConfig: false,
    shouldShowChecker: { key: "view", value: "card" },
  },
  [MACRO_KEY.WORK_ANNIVERSARY]: {
    dataKey: "data",
    updateConfigKey: "updateData",
    columnsKey: "columns",
    standaloneConfig: false,
    hasConfig: false,
    shouldShowChecker: { key: "view", value: "card" },
  },
};

const NUMBER_OF_COLUMNS = {
  [MACRO_KEY.CARDS]: [1, 2, 3, 4, 5],
  [MACRO_KEY.ANNOUNCEMENTS]: [1, 2],
  [MACRO_KEY.PEOPLE]: [1, 2, 3, 4],
  [MACRO_KEY.NEWS]: [1, 2, 3],
  [MACRO_KEY.PAGES]: [1, 2, 3, 4],
  [MACRO_KEY.SPACES]: [1, 2, 3, 4],
  [MACRO_KEY.RECENTLY_ACTIVE]: [1, 2, 3, 4],
  [MACRO_KEY.UPCOMING_BIRTHDAYS]: [1, 2, 3, 4],
  [MACRO_KEY.NEW_STARTERS]: [1, 2, 3, 4],
  [MACRO_KEY.WORK_ANNIVERSARY]: [1, 2, 3, 4],

  [undefined]: [],
};

const getNumberOfColumns = (macro, config) => {
  if (macro === MACRO_KEY.CARDS) {
    return CARD_DESIGN_COLUMNS[config?.design] || CARD_DESIGN_COLUMNS.DEFAULT;
  }

  if (macro === MACRO_KEY.NEWS) {
    if (config?.variant === "short_list") {
      return [1, 2];
    }
    return [1, 2, 3];
  }

  return NUMBER_OF_COLUMNS[macro] ?? [];
};

const getConfig = (standaloneConfig, hasConfig, state, data) => {
  if (standaloneConfig) return state?.config;
  if (!hasConfig) return data;

  return Array.isArray(data) ? data[0]?.config : data?.config;
};

const Columns = () => {
  const macro = useOpticsStore((state) => state.macro);
  const state = macroStores[macro]?.();
  const stateKeys = STATE_KEYS[macro];

  const [data, updateConfig, columnsKey, standaloneConfig, hasConfig] = [
    state?.[stateKeys?.dataKey],
    state?.[stateKeys?.updateConfigKey],
    stateKeys?.columnsKey,
    stateKeys?.standaloneConfig,
    stateKeys?.hasConfig,
  ];

  if (Array.isArray(data) && data.length === 0) return <></>;
  if (Object.keys(data || {}).length === 0) return <></>;

  const config = getConfig(standaloneConfig, hasConfig, state, data);

  const numberOfColumns = getNumberOfColumns(macro, config);

  if (stateKeys?.shouldShowChecker) {
    const { key, value } = stateKeys.shouldShowChecker;
    const currentValue = config?.[key];

    const isValid = Array.isArray(value)
      ? value.includes(currentValue)
      : currentValue === value;

    if (!isValid) return <div />;
  }

  return (
    <ColumnsContainer numberOfColumns={numberOfColumns.length}>
      {numberOfColumns.map((columns) => (
        <ColumnContainer
          key={columns}
          columns={columns}
          active={columns === config[columnsKey]}
          onClick={() => updateConfig?.({ key: columnsKey, value: columns })}
        >
          {Array.from({ length: columns }, (_, index) => (
            <Column key={index} active={columns === config[columnsKey]} />
          ))}
        </ColumnContainer>
      ))}
    </ColumnsContainer>
  );
};

export default Columns;
